import React, { useEffect, useState } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { useSolr } from '../../../hooks/useSolr';
import MandalaSkeleton from '../../common/MandalaSkeleton';
import './TermsGlossary.scss';
import { BsBook, BsQuestion, BsSearch } from 'react-icons/all';
import { usePerspective } from '../../../hooks/usePerspective';
import TermsGlossaryNav from './TermsGlossaryNav';
import * as conv from '../../TranslationTool/src/DevanagariConverter';
import { useDictionaryStore } from '../../../hooks/useDictionaryStore';
import TermsGlossarySearch from './TermsGlossarySearch';
import TermsGlossarySection from './TermsGlossarySection';

export default function TermsGlossary({ lang }) {
    // Call to glossary to start at a particular word have this format:
    //              /terms/nod/#/terms/glossary/nod/249794
    // "tid" parameter is that kmap terms id number
    // const { tid } = useParams();
    // const startAt = tid || false;
    const { startAt, searchTerm } = useDictionaryStore();
    const [chosenLetter, setChosenLetter] = useState([333901, 'अ', 'a']);
    const setPerspective = usePerspective((state) => state.setPerspective);
    lang = lang === 'nod' ? 'new' : lang;

    // Query for StartAt record to get its "cascading_position_i"
    // console.log('Start at in glossary', startAt);
    const startAtPosQuery = {
        index: 'terms',
        params: {
            q: `uid:terms-${startAt}`,
            fq: [`tree:terms`],
            rows: 1,
            fl: '*',
            sort: `position_i ASC`,
        },
    };

    let {
        isLoading: isSAPosLoading,
        data: sAPosData,
        isError: isSAPosError,
        error: sAPosError,
    } = useSolr(
        ['terms', 'glossary', lang, 'sapos', startAt],
        startAtPosQuery,
        false,
        false
    );

    // do useSolr(query key, query object), query object has index: terms, params for query

    useEffect(() => {
        if (lang === 'new') {
            setPerspective('terms', 'new.alpha');
        }
    }, []);

    useEffect(() => {
        // If specific term is to be shown, highlight its letter in the toc box
        if (startAt && sAPosData) {
            const saentry = sAPosData.docs[0];
            const letid = saentry['ancestor_uids_new.alpha'][0];
            if (chosenLetter[0] !== letid) {
                const letlat = saentry['ancestors_new.alpha'][0];
                const letdeva = conv.toDevanagari(letlat);
                setChosenLetter([letid, letdeva, letlat]);
            }
        }

        // Redirect NOD while password protected
        if (process.env.REACT_APP_STANDALONE === 'standalone') {
            const hasWpPostPassCookie = document.cookie
                .split('; ')
                .some((cookie) => cookie.startsWith('wp-postpass_'));
            if (!hasWpPostPassCookie) {
                window.location.href = '/terms/nod';
            }
        }

        window.scrollTo(0, 0);

        const treeel = document.getElementById('tab-tree-terms_new.alpha');
        // TODO: Need to generalize when implementing other glossaries

        const fulllid = `terms-${chosenLetter[0]}`;
        const treenodeid = `tab-tree-terms_new.alpha_leaf-${fulllid}`;
        const treenode = document.getElementById(treenodeid);
        if (treenode) {
            const containerRect = treeel.getBoundingClientRect();
            treeel.scrollTo({
                top: treenode.offsetTop - containerRect.top,
                behavior: 'smooth',
            });
            setTimeout(() => {
                const toggleIcon = treenode.querySelector('span.toggle-icon');
                toggleIcon.click();
            }, 500);
        }
    }, [startAt, sAPosData, chosenLetter]);

    if (isSAPosLoading) {
        return <MandalaSkeleton />;
    }

    if (isSAPosError) {
        return <div className="error red">{sAPosError}</div>;
    }

    // Cascading position integer for the start-at word
    const saPos = sAPosData?.docs[0]?.cascading_position_i;
    // console.log('startat', startAt, 'Sapos', saPos);

    const toggleNav = (divid) => {
        const divel = document.getElementById(divid);
        if (divel.classList.contains('show')) {
            divel.classList.remove('show');
        } else {
            divel.classList.add('show');
        }
    };

    return (
        <Container id="glossary">
            <Row>
                <Col>
                    <h2 className="text-center">
                        Glossary for{' '}
                        <span className="text-capitalize">
                            {lang === 'new' ? 'Newar' : 'Tibetan'}
                        </span>
                    </h2>
                </Col>
            </Row>
            <Row>
                <Col id="glossnav" lg={3} md={12}>
                    <TermsGlossaryNav
                        lang="new"
                        chosenLetter={chosenLetter}
                        chooseLetter={setChosenLetter}
                    />
                </Col>
                <Col id="definitions" lg={9} md={12}>
                    {searchTerm && <TermsGlossarySearch />}
                    {!searchTerm && chosenLetter && (
                        <TermsGlossarySection
                            letter={chosenLetter}
                            startAt={saPos}
                            key={`gloss-section-${chosenLetter.id}`}
                        />
                    )}
                </Col>
            </Row>
        </Container>
    );
}

function TermGlossHelp() {
    const [isOpen, setIsOpen] = useState(false);

    const openPopup = () => setIsOpen(true);
    const closePopup = () => setIsOpen(false);

    return (
        <>
            <BsQuestion onClick={openPopup} className="qmark" />

            {isOpen && (
                <div
                    id="glossary-help"
                    className="popup-overlay"
                    onClick={closePopup}
                >
                    <div
                        className="popup-content"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <h2>Glossary Help</h2>
                        <p>
                            The three icons by the glossary title are as
                            follows:
                        </p>
                        <ul>
                            <li>
                                <strong>
                                    <BsSearch />{' '}
                                </strong>{' '}
                                Opens the search box and letter chooser. With
                                the search box, you can begin to type a Newar
                                word in either Latin transliteration or
                                Devanagari and a list of suggestions will
                                appear. Clicking on a selection will bring you
                                to that word in the glossary. The letter chooser
                                is a block of the Newar root letters. Clicking
                                on one of those will bring you to the beginning
                                of that letter in the glossary.
                            </li>
                            <li>
                                <strong>
                                    <BsBook />{' '}
                                </strong>{' '}
                                Opens the dictionary toggle pane. This pane
                                lists all the sources dictionaries used in the
                                glossary, each one with a checkbox. If the
                                checkbox is checked, that dictionary will
                                display in the glossary. If it is not checked,
                                that dictionary is hidden from displaying and
                                searches. Whenever a dictionary title appears in
                                the glossary it is followed by a book icon.
                                Clicking on that icon will show the
                                bibliographic reference for that dictionary.
                            </li>
                            <li>
                                <strong>
                                    <BsQuestion />{' '}
                                </strong>{' '}
                                Shows this help pane.
                            </li>
                        </ul>
                        <button onClick={closePopup}>Close Help</button>
                    </div>
                </div>
            )}
        </>
    );
}
